import React, { useEffect, useRef, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";

import Login from "./pages/Login";
import Signup from "./pages/signUp";
import ForgotPassword from "./pages/ForgotPassword";
import Home from "./pages/Home";
import InProgress from "./pages/InProgress";
import Dashboard from "./pages/Dashboard";

import { logOut } from "./api/loginApiService";
import { CustomSwal } from "./services/customSwal";
import aesEncryptionService from "./services/aesEncryptionService";

// 2 hours inactivity
const LOGOUT_TIME = 2 * 60 * 60 * 1000;

/* ================= USER INITIALIZATION ================= */
const initializeUserFromSession = (): boolean => {
  const encryptedData = sessionStorage.getItem("UserDetails");

  if (!encryptedData) return false;

  const decryptedResponse = aesEncryptionService.decryptData(encryptedData);

  if (!decryptedResponse || !decryptedResponse.Result) return false;

  sessionStorage.setItem("UserName", decryptedResponse.UserName);
  sessionStorage.setItem("staff_id", decryptedResponse.User);
  sessionStorage.setItem("Mobile", decryptedResponse.Mobile);
  sessionStorage.setItem("EmailId", decryptedResponse.EmailId);
  sessionStorage.setItem("CompName", decryptedResponse.CompName);
  sessionStorage.setItem("DOB", decryptedResponse.DOB);
  sessionStorage.setItem("Department", decryptedResponse.Department);
  sessionStorage.setItem("Designation", decryptedResponse.Designation);
  sessionStorage.setItem("EVerify", decryptedResponse.EVerify);
  sessionStorage.setItem("MVerify", decryptedResponse.MVerify);

  sessionStorage.setItem(
    "CardUIData",
    JSON.stringify(decryptedResponse.CardUIData || [])
  );

  return true;
};

/* ================= PROTECTED ROUTES ================= */
const ProtectedRoutes: React.FC = () => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const staffId = sessionStorage.getItem("staff_id");

  const logout = useCallback(async () => {
    try {
      if (staffId) {
        await logOut(staffId);
      }
    } catch (err) {
      console.error("Logout Error:", err);
    }
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
  }, [staffId, navigate]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      CustomSwal.fire({
        title: "Session Timeout",
        text: "You have been inactive for too long. Please login again.",
        icon: "warning",
        confirmButtonText: "Login Again",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(() => logout());
    }, LOGOUT_TIME);
  }, [logout]);

  useEffect(() => {
    resetTimer();

    const events = [
      "mousemove",
      "keydown",
      "click",
      "scroll",
      "touchstart",
    ];

    events.forEach((event) =>
      window.addEventListener(event, resetTimer)
    );

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, resetTimer)
      );

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [resetTimer]);

  return (
    <Routes>
      <Route path="/home2" element={<Home />} />
      <Route path="/home" element={<Dashboard />} />
      <Route path="/inprogress" element={<InProgress />} />
    </Routes>
  );
};

/* ================= APP CONTENT ================= */
const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let userName = sessionStorage.getItem("UserName");

    // Rebuild session from encrypted UserDetails if needed
    if (!userName) {
      initializeUserFromSession();
      userName = sessionStorage.getItem("UserName");
    }

    const publicRoutes = [
      "/",
      "/signup",
      "/forgotpassword",
    ];

    // Not logged in
    if (!userName) {
      if (!publicRoutes.includes(location.pathname)) {
        navigate("/");
      }
      return;
    }

    // Logged in and trying to access login page
    if (userName && location.pathname === "/") {
      navigate("/home");
    }
  }, [navigate, location.pathname]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgotpassword" element={<ForgotPassword />} />

      {/* Protected Routes */}
      <Route path="/*" element={<ProtectedRoutes />} />
    </Routes>
  );
};

/* ================= ROOT ================= */
const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;